import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, AfterViewInit, OnDestroy, inject, viewChild, ViewContainerRef, Input, TemplateRef, ElementRef, DestroyRef } from '@angular/core';
import { NavigationStart, Router, Event as RouterEvent, RouterModule } from '@angular/router';
import { trigger, transition, style, animate } from '@angular/animations';
import { NavigationEnd } from '@angular/router';
import { CommonService, routes } from '../../../core.index';
import { filter, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { HeaderAiStore } from '../store/header-ai.store';
import { HeaderAiService } from '../services/header-ai.service';
import { SidebarMenuItem, UserProfile, ModuleName } from '../enums/header-ai.model';
import { StaticDialogOverlayService } from '../../ic-overlay/core/ic-overlay.service';
import { StaticDialogRef } from '../../ic-overlay/core/ic-overlay-ref';
import { environment } from '../../../../../environments/environment';


@Component({
  selector: 'app-header-v2',
  standalone: true,
  imports: [RouterModule, CommonModule],
  templateUrl: './header-v2.component.html',
  styleUrls: ['./header-v2.component.scss'],
  animations: [
    trigger('slideOverlay', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('400ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' }))
      ]),
      transition(':leave', [
        animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0, transform: 'translateY(8px)' }))
      ])
    ])
  ],
  providers: [HeaderAiStore],  
})
export class HeaderV2Component implements OnInit, AfterViewInit, OnDestroy {
  private Router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private sanitizer = inject(DomSanitizer);
  public store = inject(HeaderAiStore);
  private headerService = inject(HeaderAiService);
  private commonService = inject(CommonService);
  readonly appsMenuTemplate = viewChild.required<TemplateRef<unknown>>('appsMenuTemplate');
  readonly appsTrigger = viewChild.required<ElementRef<HTMLElement>>('appsTrigger');
  private appsDialogRef?: StaticDialogRef<any>;
  private overlayService = inject(StaticDialogOverlayService);
  private viewContainerRef = inject(ViewContainerRef);
  private destroyRef = inject(DestroyRef);
  @Input() isAppsButtonVisible: boolean = true;
  isAppsClosing = false;

  outsideClickListener: any;
  lastToggleTime = 0;
  resizeListener: any;
  public routes = routes;

  elem = document.documentElement;
  activePath = '';

  // Cleanup global listeners
  ngOnInit(): void {
    this.store.init();

    this.activePath = this.Router.url.split('/')[2];
    this.Router.events.subscribe((data: RouterEvent) => {
      if (data instanceof NavigationStart) {
        this.activePath = data.url.split('/')[2];
      }
    });

    this.store.updateModuleName(this.Router.url);
    this.Router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      tap(() => {
        this.store.updateModuleName(this.Router.url);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  onSearchApps(event: any): void {
    const q = event.target.value ? event.target.value.toLowerCase().trim() : '';
    this.store.setSearchQuery(q);
  }

  clearAppsSearch(event?: MouseEvent): void {
    event?.stopPropagation();
    this.store.setSearchQuery('');
  }

  // Update store search query from apps search input
  matchesSearch(title: string, description: string): boolean {
    if (!this.store.searchQuery()) return true;
    const q = this.store.searchQuery();
    return title.toLowerCase().includes(q) || description.toLowerCase().includes(q);
  }

  // Check if app matches current search query
  capitalizeFirstLetter(val: string | undefined | null): string {
    if (!val) return '';
    const str = String(val);
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  // Capitalize a string safely for display
  ngAfterViewInit(): void {
    setTimeout(() => {
      (window as any).HSStaticMethods?.autoInit();
    }, 150);

    this.outsideClickListener = (event: any) => {
      const target = event.target as HTMLElement;
      if (Date.now() - this.lastToggleTime < 200) return;
      const appsMenu = document.getElementById('apps_menu');
      const appsTrigger = document.getElementById('apps');
      const profileMenu = document.getElementById('profile_menu');
      const profileTrigger = document.getElementById('hs-dropdown-custom-trigger');

      if (appsMenu && (appsMenu.contains(target) || (appsTrigger && appsTrigger.contains(target)))) {
        return;
      }
      if (profileMenu && (profileMenu.contains(target) || (profileTrigger && profileTrigger.contains(target)))) {
        return;
      }

      this.closeMenu('apps');
      this.closeMenu('profile');
      this.store.closeAll();
      this.cdr.detectChanges();
    };
    document.addEventListener('click', this.outsideClickListener);
  }

  ngOnDestroy(): void {
    if (this.outsideClickListener) {
      document.removeEventListener('click', this.outsideClickListener);
    }
  }

  get moduleName(): ModuleName {
    return this.store.moduleName();
  }

  get sidebarMenu(): SidebarMenuItem[] {
    return this.store.sidebarMenu();
  }

  safeIcon(html: string | undefined): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  get userProfile(): UserProfile | null {
    return this.store.userProfile();
  }

  get firstname(): string {
    return this.store.firstname();
  }
  get lastname(): string {
    return this.store.lastname();
  }
  get orgUnits(): any[] {
    return this.store.orgUnits();
  }

  get allowExpenseModule(): boolean {
    return this.store.allowExpenseModule();
  }

  get isAppsOpen(): boolean {
    return this.store.isAppsOpen();
  }

  get isProfileOpen(): boolean {
    return this.store.isProfileOpen();
  }

  toggleDropdown(event: any, name: string): void {
    event.stopPropagation();
    this.lastToggleTime = Date.now();
    if (name === 'apps') {
      if (this.store.isAppsOpen()) {
        this.closeMenu('apps');
        return;
      }
      this.store.isAppsOpen.set(true);
      if (this.store.isProfileOpen()) {
        this.store.isProfileOpen.set(false);
      }
      this.appsDialogRef = this.overlayService.openConnected(
        this.appsMenuTemplate(),
        this.viewContainerRef,
        this.appsTrigger().nativeElement,
        { hasBackdrop: false }
      );
    }

    if (name === 'profile') {
      this.store.toggleProfile();
      if (this.store.isProfileOpen()) {
        this.store.isAppsOpen.set(false);
      }
    }

    const appsMenu = document.getElementById('apps_menu');
    const appsTrigger = document.getElementById('apps');

    if (appsTrigger) appsTrigger.setAttribute('aria-expanded', String(this.store.isAppsOpen()));

    // Clear any inline styles that might have been set previously by JS
    if (appsMenu && !this.store.isAppsOpen()) {
      appsMenu.style.left = '';
      appsMenu.style.right = '';
      appsMenu.style.width = '';
      appsMenu.style.transform = '';
    }
  }

  // CSS is responsible for positioning the apps dropdown; JS positioning removed

  private closeMenu(name: string): void {
    if (name === 'apps') {
      this.store.isAppsOpen.set(false);
      if (this.appsDialogRef) {
        this.isAppsClosing = true;
        const ref = this.appsDialogRef;
        this.appsDialogRef = undefined;
        setTimeout(() => {
          ref.close('cancel');
          this.isAppsClosing = false;
        }, 300);
      }
      const menu = document.getElementById('apps_menu');
      const trigger = document.getElementById('apps');
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    }
    if (name === 'profile') {
      this.store.isProfileOpen.set(false);
    }
  }

  navigateTo(route?: string): void {
    this.closeOffcanvasMenu();
    if (!route) return;
    this.store.setSearchQuery('');
    this.store.isAppsOpen.set(false);
    const trigger = document.getElementById('apps');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');

    this.lastToggleTime = Date.now();
    this.Router.navigate([route]);
  }

  toggleSubRoute(item: any): void {
    // Expand/collapse sidebar subroutes
    item.showSubRoute = !item.showSubRoute;
  }

  closeOffcanvasMenu() {
    // Close mobile offcanvas if open
    const closeButton = document.getElementById('closeButton');
    if (closeButton) {
      closeButton.click();
    }

    const backdrop = document.getElementById('offcanvas-menu-backdrop');
    if (backdrop) {
      backdrop.remove()
    }
  }

  shouldShowMenuButton(): boolean {
    // Hide mobile menu button on Account module
    return this.moduleName !== 'Account';
  }

  fullscreen() {
    // Toggle browser fullscreen
    if (!document.fullscreenElement) {
      this.elem.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }

  toAccount() {
    // Navigate to My Account and clear app search
    this.store.setSearchQuery('');
    this.Router.navigate([routes.account]);
  }

  canAccessVMS(): boolean {
    return this.headerService.canAccessModule('vms');
  }

  canAccessAdmin(): boolean {
    return this.headerService.canAccessModule('admin');
  }

  canAccessAccount(): boolean {
    return this.headerService.canAccessModule('account');
  }
  canAccessGrievance(): boolean {
    return this.headerService.canAccessModule('grievance');
  }

  canAccessExpense(): boolean {
    return this.headerService.canAccessModule('expense');
  }

  canAccessConferenceRoom(): boolean {
    return this.headerService.canAccessModule('conference-room');
  }

  canAccessApprovalNotes(): boolean {
    return this.headerService.canAccessModule('approval-notes');
  }
  
  canAccessDocuments(): boolean {
    return this.headerService.canAccessModule('dms');
  }

  toDocuments(): void {
    this.closeMenu('apps');
    this.store.setSearchQuery('');
    this.Router.navigate(['/dms']);
    this.store.moduleName.set('Documents');
  }

  // Updated navigation methods
  toVms() {
    const route = this.store.getFirstAccessibleRoute('vms');
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    this.Router.navigate([route]);
    this.store.moduleName.set('VMS');
  }

  toAdmin() {
    // Admin navigation with permission checks
    const route = this.store.getFirstAccessibleRoute('admin');
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    this.Router.navigate([route]);
    this.store.moduleName.set('Admin');
  }

  // Grievance navigation with permission checks
  toGrievance() {
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    if (localStorage.getItem('self') === 'true') {
      const orgUnits = this.userProfile?.org_units || [];
      if (orgUnits.length > 0) {
        this.Router.navigate(['/grievance/employee/portal']);
      } else {
        this.Router.navigate(['/apps']);
      }
      this.store.moduleName.set('Grievance');
      return;
    }

    const moduleNames = this.userProfile?.roles?.module_names || [];

    if (!moduleNames.includes('grievance')) {
      const orgUnits = this.userProfile?.org_units || [];
      if (orgUnits.length > 0) {
        this.Router.navigate(['/grievance/employee/portal']);
      } else {
        this.Router.navigate(['/apps']);
      }
      this.store.moduleName.set('Grievance');
      return;
    }
    const permissions = this.headerService.getUserPermissions();
    const hasGrievancePermission = permissions.some(p =>
      p === 'grievance-approval' || p === 'grievance-settings'
    );

    if (hasGrievancePermission) {
      const route = this.store.getFirstAccessibleRoute('grievance');
      this.Router.navigate([route]);
    } else {
      this.Router.navigate(['/grievance/no-permission']);
    }
    this.store.moduleName.set('Grievance');
  }

  // Expense navigation with permission checks
  toExpense() {
    const route = this.store.getFirstAccessibleRoute('expense');
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    this.Router.navigate([route]);
    this.store.moduleName.set('Trip And Expense');
  }

  toConferenceRoom() {
    const route = this.store.getFirstAccessibleRoute('conference-room');
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    this.Router.navigate([route]);
    this.store.moduleName.set('Conference Room');
  }

  getAccessibleModulesCount(): number {
    const modules = [
      this.canAccessAccount(),
      this.canAccessVMS(),
      this.canAccessAdmin(),
      this.canAccessGrievance(),
      this.canAccessExpense(),
      this.canAccessConferenceRoom(),
      this.canAccessApprovalNotes(),
      this.canAccessDocuments()
    ];
    if (this.headerService.isAdmin()) {
      return modules.length
    }
    return modules.filter(Boolean).length;
  }

  toLogout() {
    this.commonService.toLogout();
  }

  isAdminLogin(): boolean {
    return this.headerService.isAdmin();
  }

  isGrievanceModuleAccess(): boolean {
    const moduleNames = this.userProfile?.roles?.module_names || [];
    return moduleNames.includes('grievance');
  }

  isRaiseGrievanceAccess(): boolean {
    const permissions = this.headerService.getUserPermissions();
    return permissions.includes('raise-grievance-access');
  }

  toAiBuddy() {
    const route = this.store.getFirstAccessibleRoute('ai-buddy');
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    this.Router.navigate([route]);
    this.store.moduleName.set('AI Buddy');
  }

  isAiBuddyEnabled(): boolean {
    return environment.clo_ai_buddy;
  }

  toApprovalNotes() {
    const route = this.store.getFirstAccessibleRoute('approval-notes');
    this.store.isAppsOpen.set(false);
    const menu = document.getElementById('apps_menu');
    const trigger = document.getElementById('apps');
    if (menu && !menu.classList.contains('hidden')) menu.classList.add('hidden');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    this.lastToggleTime = Date.now();
    this.store.setSearchQuery('');
    this.Router.navigate([route || '/approval-notes/dashboard']);
    this.store.moduleName.set('Approval Notes');
  }
}
